import { useEffect, useState } from 'react'
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore'
import { db } from '@fleetops/firebase'
import { firestorePaths } from '@fleetops/types'
import type { MaintenanceRecord, MaintenanceComment } from '@fleetops/types'
import { useSession } from '@/store/auth.store'
import { subscriptionError, warnIfTruncated } from '@/lib/firestoreError'

// Bounded realtime window. Proper pagination is the follow-up; until then a
// tenant that exceeds this gets a console warning rather than silent loss.
const MAINTENANCE_WINDOW = 500

export function useMaintenanceRecords() {
  const session = useSession()
  const [records, setRecords] = useState<MaintenanceRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!session) return
    // Ordered DESCENDING on purpose. With `asc` + `limit`, the window holds
    // the 200 *oldest* records — so once a tenant passes 200, a revision
    // scheduled for a future date is precisely the one that falls outside it,
    // and "agendei uma revisão e não aparece na Manutenção" is the result.
    // Descending keeps every scheduled/future record and drops old completed
    // ones off the far end instead, which is the right thing to lose.
    // Display order is unaffected: the page sorts client-side (sortRecords).
    const q = query(
      collection(db, firestorePaths.maintenanceRecords(session.tenantId)),
      where('tenantId', '==', session.tenantId),
      orderBy('scheduledDate', 'desc'),
      limit(MAINTENANCE_WINDOW),
    )
    return onSnapshot(q, (snap) => {
      warnIfTruncated('as revisões de manutenção', snap.size, MAINTENANCE_WINDOW)
      setRecords(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as MaintenanceRecord))
      setIsLoading(false)
    }, subscriptionError('as revisões de manutenção', () => setIsLoading(false)))
  }, [session?.tenantId])

  return { records, isLoading }
}

export function useTrailerMaintenanceRecords(trailerId: string) {
  const session = useSession()
  const [records, setRecords] = useState<MaintenanceRecord[]>([])

  useEffect(() => {
    if (!session || !trailerId) return
    const q = query(
      collection(db, firestorePaths.maintenanceRecords(session.tenantId)),
      where('tenantId', '==', session.tenantId),
      where('trailerId', '==', trailerId),
      orderBy('scheduledDate', 'asc'),
    )
    return onSnapshot(q, (snap) => {
      setRecords(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as MaintenanceRecord))
    }, subscriptionError('as revisões do reboque'))
  }, [session?.tenantId, trailerId])

  return records
}

// Staff-side comment thread for a maintenance record — mirrors
// useDamageComments. Includes internal-only comments (visibleToSupplier:
// false); the supplier only ever sees this via the Worker's filtered
// endpoint, never this direct listener.
export function useMaintenanceComments(recordId: string) {
  const session = useSession()
  const [comments, setComments] = useState<MaintenanceComment[]>([])

  useEffect(() => {
    if (!session || !recordId) return
    const q = query(
      collection(db, firestorePaths.maintenanceComments(session.tenantId, recordId)),
      orderBy('createdAt', 'asc'),
    )
    return onSnapshot(q, (snap) => {
      setComments(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as MaintenanceComment))
    }, subscriptionError('os comentários da revisão'))
  }, [session?.tenantId, recordId])

  return comments
}
